/**
 * HardResetCard - Bouton de reset d'urgence avec sémaphore de génération
 * Extrait de Profile.js pour améliorer la maintenabilité
 * AMIRAUX UNIQUEMENT
 */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { AlertTriangle, Loader2, User, Clock, Zap } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { API, toast } from '../../App';

export const HardResetCard = ({ hardResetConfirm, setHardResetConfirm }) => {
  const [generationLock, setGenerationLock] = useState({ locked: false });
  const [loading, setLoading] = useState(true);
  
  // Refs pour le polling (évite les re-renders en cascade)
  const lockPollRef = useRef(null);
  const lockPollRateRef = useRef(10000);

  // Polling du statut de génération - chargement initial
  useEffect(() => {
    const fetchLockStatus = async () => {
      try {
        const res = await axios.get(`${API}/generation/status`);
        setGenerationLock(res.data || { locked: false });
      } catch (e) { /* ignore */ }
      setLoading(false);
    };
    fetchLockStatus();
    lockPollRef.current = setInterval(fetchLockStatus, 10000);
    lockPollRateRef.current = 10000;
    
    return () => {
      if (lockPollRef.current) {
        clearInterval(lockPollRef.current);
      }
    };
  }, []);

  // Ajuster la fréquence quand locked change
  useEffect(() => {
    const targetRate = generationLock.locked ? 3000 : 10000;
    
    if (lockPollRateRef.current !== targetRate) {
      const fetchLockStatus = async () => {
        try {
          const res = await axios.get(`${API}/generation/status`);
          setGenerationLock(res.data || { locked: false });
        } catch (e) { /* ignore */ }
      };
      
      if (lockPollRef.current) {
        clearInterval(lockPollRef.current);
      }
      lockPollRef.current = setInterval(fetchLockStatus, targetRate);
      lockPollRateRef.current = targetRate;
    }
  }, [generationLock.locked]);

  const handleHardReset = async () => {
    setHardResetConfirm(false);
    try {
      const res = await axios.post(`${API}/admin/hard-reset`);
      const cancelled = res.data.cancelled_jobs || 0;
      const lockReleased = res.data.lock_released;
      if (cancelled > 0 || lockReleased) {
        toast.success(`Reset effectué : ${cancelled} job(s) annulé(s)${lockReleased ? ', verrou libéré' : ''} — redémarrage en cours`);
      } else {
        toast.success('Reset effectué : aucun job en cours — redémarrage en cours');
      }
      setTimeout(() => {
        toast.info('Rechargement de la page...');
        setTimeout(() => window.location.reload(), 5000);
      }, 3000);
    } catch (e) {
      toast.error('Erreur lors du reset');
    }
  };

  // Helper pour formater l'étape
  const getStepLabel = (step) => {
    const labels = {
      'preparing': 'Préparation',
      'queued': 'File d\'attente',
      'generating_text': 'Génération texte',
      'generating_image': 'Génération image',
      'uploading': 'Upload WordPress',
      'finalizing': 'Finalisation',
    };
    return labels[step] || step || 'En cours';
  };

  return (
    <Card className="relative bg-red-950/20 backdrop-blur-xl border-red-800/40">
      <CardHeader>
        <CardTitle className="text-xl md:text-2xl font-orbitron uppercase tracking-wider text-red-400 flex items-center">
          <AlertTriangle className="w-5 h-5 md:w-6 md:h-6 mr-2 md:mr-3" />
          Hard Reset
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Sémaphore de génération */}
        <div className={`p-3 rounded-lg border ${
          loading 
            ? 'bg-slate-800/50 border-slate-700' 
            : generationLock.locked 
            ? 'bg-red-950/40 border-red-500/50' 
            : 'bg-emerald-950/30 border-emerald-500/40'
        }`}>
          <div className="flex items-center gap-3">
            <div className={`w-4 h-4 rounded-full flex-shrink-0 ${
              loading 
                ? 'bg-slate-500 animate-pulse' 
                : generationLock.locked 
                ? 'bg-red-500 animate-pulse' 
                : 'bg-emerald-500'
            }`} />
            <div className="flex-1 min-w-0">
              {loading ? (
                <span className="text-sm text-slate-400">Vérification du statut...</span>
              ) : generationLock.locked ? (
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-sm font-semibold text-red-300">
                    <Zap className="w-4 h-4" />
                    GÉNÉRATION EN COURS
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-red-200/80">
                    {generationLock.user_name && (
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {generationLock.user_name}
                      </span>
                    )}
                    {generationLock.job_name && (
                      <span className="truncate max-w-[200px]" title={generationLock.job_name}>
                        "{generationLock.job_name}"
                      </span>
                    )}
                    {generationLock.total_sites > 0 && (
                      <span className="font-mono">
                        {generationLock.current_site || 0}/{generationLock.total_sites} sites
                      </span>
                    )}
                    {generationLock.current_step && (
                      <span className="text-amber-300">
                        {getStepLabel(generationLock.current_step)}
                      </span>
                    )}
                    {generationLock.progress_percent != null && (
                      <span className="font-bold text-red-300">
                        {generationLock.progress_percent}%
                      </span>
                    )}
                  </div>
                </div>
              ) : (
                <span className="text-sm font-medium text-emerald-300">
                  ✓ Aucune génération en cours — système libre
                </span>
              )}
            </div>
          </div>
        </div>

        <p className="text-sm text-slate-400">
          Annule tous les jobs en cours, libère le verrou de génération et redémarre l&apos;application. 
          Utilisez en cas de bug, de génération bloquée ou de délai anormalement long. 
          La configuration et les articles existants ne sont pas affectés.
        </p>
        
        {!hardResetConfirm ? (
          <Button
            onClick={() => setHardResetConfirm(true)}
            className="bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/50 hover:border-red-400"
            data-testid="btn-hard-reset"
          >
            <AlertTriangle className="w-4 h-4 mr-2" />
            Hard Reset
          </Button>
        ) : (
          <div className="flex items-center gap-3 p-3 bg-red-950/30 border border-red-500/40 rounded-lg">
            <span className="text-sm text-red-300 font-semibold">Confirmer le redémarrage ?</span>
            <Button
              onClick={handleHardReset}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="btn-hard-reset-confirm"
            >
              Oui, redémarrer
            </Button>
            <Button
              onClick={() => setHardResetConfirm(false)}
              variant="outline"
              className="border-slate-600 text-slate-400 hover:text-slate-200"
            >
              Annuler
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default HardResetCard;
